import emailjs from "@emailjs/browser";
import {
  ArrowRight,
  Calendar,
  Clock,
  Image,
  Mail,
  MapPin,
  Menu,
  Users,
  X,
  HeartPlus,
} from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/api";
import logo from "../assets/logo.png";
import "./ClinicHome.css";
import LoadingSpinner from "./LoadingSpinner";
import GradientBlinds from './GradientBlinds';
import GalleryCarousel from './GalleryCarousel';
import DirectionCard from './DirectionCard';




const ClinicHome = () => {
  const { locationSlug } = useParams();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const [location, setLocation] = React.useState(null);
  const [content, setContent] = React.useState(null);
  const [gallery, setGallery] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [contactForm, setContactForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = React.useState(false);
  const [contactSuccess, setContactSuccess] = React.useState(false);
  const [contactError, setContactError] = React.useState("");

  React.useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const locs = await api.getLocations();
        const found = locs.find((l) => l.slug === locationSlug);
        if (mounted && found) setLocation(found);
      } catch (err) {
        console.warn("Failed to fetch locations, using mock", err);
      }

      try {
        const res = await api.getContent(locationSlug);
        if (mounted && res) setContent(res);
      } catch (err) {
        console.warn(
          "Failed to load content for",
          locationSlug,
          "using mock",
          err
        );
      }

      try {
        const galleryRes = await api.getGallery(locationSlug);
        if (mounted && galleryRes) setGallery(galleryRes);
      } catch (err) {
        console.warn("Failed to fetch gallery, using mock", err);
      }

      if (mounted) setLoading(false);
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  // Contact form handlers
  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value,
    });
    setContactError("");
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Validation: at least email or phone required
    if (!contactForm.email.trim() && !contactForm.phone.trim()) {
      setContactError(
        "Veuillez fournir au moins un email ou un numéro de téléphone."
      );
      return;
    }

    setContactLoading(true);
    setContactError("");

    try {
      // First validate with backend and get location email
      const response = await api.sendContactMessage({
        locationSlug: locationSlug,
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: contactForm.message,
      });

      // Send email using EmailJS
      const templateParams = {
        to_email: response.locationEmail,
        from_name: contactForm.name,
        reply_to: contactForm.email || "Non fourni",
        user_phone: contactForm.phone || "Non fourni",
        message: contactForm.message,
        location_name: location.name,
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setContactSuccess(true);
      setContactForm({ name: "", email: "", phone: "", message: "" });

      // Close modal after 2 seconds
      setTimeout(() => {
        setIsContactModalOpen(false);
        setContactSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Contact form error:", err);
      setContactError(
        err.response?.data?.detail ||
          "Une erreur s'est produite. Veuillez réessayer."
      );
    } finally {
      setContactLoading(false);
    }
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setContactForm({ name: "", email: "", phone: "", message: "" });
    setContactError("");
    setContactSuccess(false);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMenuOpen(false);
    }
  };
  

  // Show loading state while data is being fetched
  if (!location || !content) {
      return <LoadingSpinner />;
  }

  return (
    <div className="clinic-home">
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="container">
          <div className="nav-content">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img
                src={logo}
                alt="Sharp Physio logo"
                style={{ width: 40, height: 40, objectFit: "contain" }}
              />
              <span className="nav-title">SHARP PHYSIO</span>
            </div>
            <button
              className="mobile-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className={`nav-links ${menuOpen ? "open" : ""}`}>
              <button
                className="nav-link"
                onClick={() => navigate(`/${locationSlug}/sessions`)}
              >
                Séances
              </button>
              <button
                className="nav-link"
                onClick={() => navigate(`/${locationSlug}/team`)}
              >
                L'Équipe
              </button>
              <button
                className="nav-link"
                onClick={() => navigate(`/${locationSlug}/gallery`)}
              >
                Galerie
              </button>
              <button
                className="nav-link"
                onClick={() => scrollToSection("contact")}
              >
                Contact
              </button>
              <a
                href={location.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-nav"
              >
                <Calendar size={18} /> Prendre RDV
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <GradientBlinds
            gradientColors={['#FF9FFC', '#5227FF']}
            angle={0}
            noise={0.3}
            blindCount={12}
            blindMinWidth={50}
            spotlightRadius={0.5}
            spotlightSoftness={1}
            spotlightOpacity={1}
            mouseDampening={0.15}
            distortAmount={0}
            shineDirection="left"
            mixBlendMode="lighten"
            style={{ pointerEvents: 'auto' }}
          />
        </div>

        <div
          className="hero-content"
          style={{
            position: 'relative',
            zIndex: 1,
            height: '800px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <h1 className="heading-xl">{location.name}</h1>
          <p className="body-lg hero-subtitle">
            Votre cabinet de kinésithérapie de confiance au cœur de Paris
          </p>
          <div className="hero-cta">
            <a
              href={location.doctolib}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-large"
              style={{ pointerEvents: 'auto' }}
            >
              <Calendar size={20} /> Réserver sur Doctolib
            </a>
          </div>
        </div>

      </section>


      

      {/* Quick Tiles Section */}
      <section className="tiles-section section">
        
        <div className="container">
          <div className="tiles-grid">
            <div className="tile" onClick={() => navigate(`/${locationSlug}/sessions`)}>
              <div className="tile-icon">
                <HeartPlus size={32} />
              </div>
              <h3 className="heading-sm">Nos Séances</h3>
              <p className="body-md">Découvrez le déroulement de nos séances</p>
            </div>

            <div className="tile" onClick={() => navigate(`/${locationSlug}/team`)}>
              <div className="tile-icon">
                <Users size={32} />
              </div>
              <h3 className="heading-sm">Notre Équipe</h3>
              <p className="body-md">Rencontrez nos kinésithérapeutes</p>
            </div>

            <div className="tile" onClick={() => navigate(`/${locationSlug}/gallery`)}>
              <div className="tile-icon">
                <MapPin size={32} />
              </div>
              <h3 className="heading-sm">Nos Locaux</h3>
              <p className="body-md">Visitez notre cabinet moderne</p>
            </div>

            <div className="tile" onClick={() => scrollToSection("contact")}>
              <div className="tile-icon">
                <Clock size={32} />
              </div>
              <h3 className="heading-sm">Horaires & Contact</h3>
              <p className="body-md">Toutes les informations pratiques</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="mission-section section">
        <div className="container">
          <div className="mission-content">
            <h2 className="heading-lg">{content.mission.title}</h2>
            <p className="body-lg mission-text" style={{ textAlign: "justify" }}>{content.mission.content}</p>

            <div className="values-grid">
              {content.values.map((value, index) => (
                <div key={index} className="value-card">
                  <h3 className="heading-sm">{value.title}</h3>
                  <p className="body-md" style={{textJustify: "inter-word" }}>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Séances - Navigate to separate page */}
      <section id="seances" className="seances-preview-section section">
        <div className="container">
            <div className="services-section">
              <h3 className="heading-lg text-center">Nos Spécialités</h3>
              <div className="services-grid">
                {content.services.map((service, index) => (
                  <div key={index} className="service-item">
                    <span className="service-bullet">•</span>
                    <span>{service}</span>
                  </div>
                ))}
              </div>
            </div>
        </div>
      </section>

      {/* Gallery Section */}
      <GalleryCarousel
        images={gallery.map(item => item.url)}
        itemWidth={12}
        itemHeight={20}
        gap={1.2}
        perspective={50}
        hoverScale={15}
        transitionDuration={1.25}
        grayscaleStrength={1}
        brightnessLevel={0.5}
        activeWidth={45}
        enableKeyboardNavigation={true}
        autoPlay={false}
        autoPlayDelay={3000}
      />
      {/* Contact Section */}
      <section id="contact" className="contact-section section">
        <div className="container">
          <h2 className="heading-lg text-center">Informations Pratiques</h2>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="info-item">
                <MapPin size={24} />
                <div>
                  <h4 className="heading-sm">Adresse</h4>
                  <p className="body-md">{location.address}</p>
                </div>
              </div>

              <div className="info-item">
                <Clock size={24} />
                <div>
                  <h4 className="heading-sm">Horaires</h4>
                  {location.hours.map((schedule, index) => (
                    <p key={index} className="body-md">
                      <strong>{schedule.day}:</strong> {schedule.time}
                    </p>
                  ))}
                </div>
              </div>

              <div className="info-item">
                <button
                  className="contact-button"
                  onClick={() => setIsContactModalOpen(true)}
                >
                  <Mail size={24} />
                  <div>
                    <h4 className="heading-sm">Contactez-nous</h4>
                    <p className="body-md">Envoyez-nous un message</p>
                  </div>
                </button>
              </div>

              <a
                href={location.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ marginTop: "2rem" }}
              >
                <Calendar size={20} /> Prendre rendez-vous
              </a>
            </div>

            <div className="contact-right">
              <div className="map-container">
                <iframe
                  src={location.mapEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: "8px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location map"
                ></iframe>
              </div>

              {location.showDirectionCard && <DirectionCard />}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src={logo}
                alt="Sharp Physio logo"
                style={{
                  width: 50,
                  height: 50,
                  objectFit: "contain",
                  marginRight: "8px",
                }}
              />
              <span>SHARP PHYSIO</span>
            </div>
            <div className="footer-links">
              <button onClick={() => navigate("/privacy")}>
                Politique de confidentialité
              </button>
              <button onClick={() => navigate("/cookies")}>Cookies</button>
              <button onClick={() => navigate("/legal")}>
                Mentions légales
              </button>
              <button onClick={() => navigate("/admin")}>Admin</button>
            </div>
            <p className="footer-copyright">
              © {new Date().getFullYear()} Sharp Physio. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="contact-modal-overlay" onClick={closeContactModal}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeContactModal}>
              <X size={24} />
            </button>

            <h3 className="heading-md">Contactez-nous</h3>

            {contactSuccess ? (
              <div className="contact-success">
                <p>✓ Message envoyé avec succès!</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Nom *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactFormChange}
                    required
                    placeholder="Votre nom"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactFormChange}
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <p className="form-note">
                  * Au moins un email ou un téléphone est requis
                </p>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    required
                    rows="5"
                    placeholder="Votre message..."
                  ></textarea>
                </div>

                {contactError && (
                  <div className="contact-error">{contactError}</div>
                )}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={contactLoading}
                >
                  {contactLoading ? "Envoi..." : "Envoyer"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicHome;
